// workplaneGeometry — вычисление мирового базиса рабочей плоскости.
//
// Любая рабочая плоскость (со смещением / под углом / срединная) приводится
// к единому виду CUSTOM: { origin:[x,y,z], xDir:[x,y,z], normal:[x,y,z] }.
// Этот базис понимает система эскизов (planeRef.plane === 'CUSTOM') и ядро
// (replicad.Plane) — тот же путь, что и для эскиза на грани тела.
//
// Базовые плоскости (xDir/yDir/normal), согласованы с PlanePicker.planeQuat:
//   XY: x=[1,0,0]  y=[0,1,0]  n=[0,0,1]
//   XZ: x=[1,0,0]  y=[0,0,1]  n=[0,-1,0]
//   YZ: x=[0,1,0]  y=[0,0,1]  n=[1,0,0]

const BASE = {
    XY: { xDir: [1, 0, 0], yDir: [0, 1, 0], normal: [0, 0, 1] },
    XZ: { xDir: [1, 0, 0], yDir: [0, 0, 1], normal: [0, -1, 0] },
    YZ: { xDir: [0, 1, 0], yDir: [0, 0, 1], normal: [1, 0, 0] },
};

const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const cross = (a, b) => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
];
const norm = (a) => {
    const l = Math.hypot(a[0], a[1], a[2]) || 1;
    return [a[0] / l, a[1] / l, a[2] / l];
};

// Повернуть вектор v вокруг единичной оси k на угол theta (рад) — Родригес.
function rotateAround(v, k, theta) {
    const c = Math.cos(theta), s = Math.sin(theta);
    const kv = cross(k, v);
    const kk = k[0] * v[0] + k[1] * v[1] + k[2] * v[2];
    return [
        v[0] * c + kv[0] * s + k[0] * kk * (1 - c),
        v[1] * c + kv[1] * s + k[1] * kk * (1 - c),
        v[2] * c + kv[2] * s + k[2] * kk * (1 - c),
    ];
}


/**
 * Базис рабочей плоскости → { origin, xDir, normal } в мировых координатах.
 * Возвращает null, если тип не распознан.
 *
 * Поддерживаемые формы workplane:
 *   { kind:'offset'|undefined, basePlane, offset }
 *   { kind:'angle', basePlane, rotAxis:'X'|'Y', angle }  (угол в градусах)
 *   { kind:'midplane', basePlane, offset1, offset2 }
 *   { kind:'custom', origin, xDir, normal }
 */
export function workplaneBasis(wp) {
    if (!wp) return null;
    if (wp.kind === 'custom' && wp.origin && wp.normal) {
        return { origin: wp.origin, xDir: wp.xDir || null, normal: wp.normal };
    }
    const base = BASE[wp.basePlane || 'XY'];
    if (!base) return null;

    // --- Плоскость под углом ---
    if (wp.kind === 'angle') {
        // Ось поворота лежит в базовой плоскости: X → её xDir, Y → её yDir.
        const axis = norm(wp.rotAxis === 'Y' ? base.yDir : base.xDir);
        const theta = (Number(wp.angle) || 0) * Math.PI / 180;
        const xDir = norm(rotateAround(base.xDir, axis, theta));
        const normal = norm(rotateAround(base.normal, axis, theta));
        // origin — начало координат (плоскость проходит через ось поворота
        // в origin). Можно позже добавить точку опоры.
        return { origin: [0, 0, 0], xDir, normal };
    }

    // --- Плоскость через 3 точки ---
    if (wp.kind === 'three_points' && wp.p1 && wp.p2 && wp.p3) {
        const p1 = wp.p1, p2 = wp.p2, p3 = wp.p3;
        const v1 = [p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]];
        const v2 = [p3[0] - p1[0], p3[1] - p1[1], p3[2] - p1[2]];
        const normal = norm(cross(v1, v2));
        const xDir = norm(v1);
        // origin — первая точка.
        return { origin: [...p1], xDir, normal };
    }

    // --- Срединная плоскость ---
    if (wp.kind === 'midplane') {
        const mid = ((Number(wp.offset1) || 0) + (Number(wp.offset2) || 0)) / 2;
        const origin = scale(base.normal, mid);
        return { origin, xDir: base.xDir, normal: base.normal };
    }

    // --- Плоскость со смещением (по умолчанию) ---
    const off = Number(wp.offset) || 0;
    return {
        origin: scale(base.normal, off),
        xDir: base.xDir,
        normal: base.normal,
    };
}


/**
 * planeRef для создания эскиза на рабочей плоскости.
 * Для чистого offset возвращаем именованную плоскость (basePlane+offset) —
 * так эскиз рисуется в привычной 2D-системе. Для угловых/срединных/custom —
 * CUSTOM с полным базисом.
 */
export function workplaneToPlaneRef(wp) {
    if (!wp) return null;
    // Простое смещение по базовой плоскости — именованная плоскость.
    if ((!wp.kind || wp.kind === 'offset')) {
        return { plane: wp.basePlane || 'XY', offset: Number(wp.offset) || 0 };
    }
    const basis = workplaneBasis(wp);
    if (!basis) return null;
    return {
        plane: 'CUSTOM',
        origin: basis.origin,
        xDir: basis.xDir,
        normal: basis.normal,
    };
}


export { BASE as WORKPLANE_BASES };
